import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {createUseStyles} from 'react-jss'
import {PayrollFileRow} from "../../redux/types"
import ListItemCard from "../ListItemCard"
import Pagination from '../Pagination'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrashAlt} from "@fortawesome/free-solid-svg-icons";
import {Colors} from "../../constants/colors";
import {useAppSelector} from "../../redux/hooks";

interface Props {
  rows: PayrollFileRow[]
  pageIndex: number
  pageSize: number
  onPageChange: (pageIndex: number) => void
  onDeleteRow?: (row: PayrollFileRow) => void
}

const PayrollFileRowList = ({rows, pageIndex, pageSize, onPageChange, onDeleteRow}: Props) => {
  const styles = useStyles()
  const {t} = useTranslation(['common', 'payrolls']);

  const customer = useAppSelector(state => state.customer.customer)
  const payrollFileRowDeletionEnabled = customer?.employer?.payrollFileRowDeletionEnabled ?? false

  const itemsOnCurrentPage = useMemo(() => {
    const firstPageIndex = (pageIndex - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return rows.slice(firstPageIndex, lastPageIndex);
  }, [rows, pageIndex, pageSize])

  const renderHeader = () => {
    return (
      <ListItemCard className={styles.listItemContainer}>
        <div className={styles.idContainer}>
          <strong>{t('idNumber', {ns: 'common'})}</strong>
        </div>
        <div className={styles.nameContainer}>
          <strong>{t('name', {ns: 'common'})}</strong>
        </div>
        <div className={styles.amountContainer}>
          <strong>{t('netAmount', {ns: 'payrolls'})}</strong>
        </div>
        <div className={styles.iconContainer}/>
      </ListItemCard>
    )
  }

  const renderListItems = () => {
    return itemsOnCurrentPage?.map((row, index) => {
      return (
        <ListItemCard key={`row${index}`} className={styles.listItemContainer} index={index}>
          <div className={styles.idContainer}>
            {row.employeeIdentificationNumber}
          </div>
          <div className={styles.nameContainer}>
            {row.employeeName}
          </div>
          <div className={styles.amountContainer}>
            {row.netAmount.amount.toLocaleString('sv-SE', {minimumFractionDigits: 2})} {row.netAmount.currencyCode.toUpperCase()}
          </div>
          <div className={styles.iconContainer}>
            {onDeleteRow && payrollFileRowDeletionEnabled &&
                <FontAwesomeIcon
                    className={styles.button}
                    icon={faTrashAlt}
                    color={Colors.gray1}
                    title={t('delete', {ns: 'common'})}
                    onClick={() => onDeleteRow(row)}
                />
            }
          </div>
        </ListItemCard>
      )
    })
  }

  return (
    <>
      {renderHeader()}
      {renderListItems()}
      <div className={styles.pagination}>
        <Pagination
          pageIndex={pageIndex}
          itemCount={rows.length}
          pageSize={pageSize}
          siblingCount={1}
          onPageChange={onPageChange}
        />
      </div>
    </>
  )
}

export default PayrollFileRowList

const useStyles = createUseStyles({
  listItemContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'space-between',
  },
  idContainer: {
    width: 150
  },
  nameContainer: {
    flexGrow: 1
  },
  amountContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: 120
  },
  iconContainer: {
    marginLeft: 10,
    width: 30
  },
  button: {
    marginRight: 15,
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro_600,
      cursor: 'pointer',
    }
  },
  pagination: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center'
  }
})
